import {
  CELL,
  GRID_SIZE,
  LAND_TONE_SALT,
  SPAN,
  WATER_GLINT_SALT,
  WATER_RIPPLE_SALT,
} from '../game/constants';
import { dayNumber } from '../game/daily';
import { cx, cy, key, parseKey } from '../game/geometry';
import { network, towerRoles } from '../game/network';
import { mulberry32 } from '../game/rng';
import type { Network, Point, Puzzle } from '../game/types';
import { Inhabitants } from './birds';
import { drawFlora } from './flora';
import { canHover, prefersReducedMotion } from './motion';
import { shorelinePath, type ShoreMargins } from './shore';
import { SPRITE, SPRITE_SCALE, TERRAIN_VARIANTS, buildDefs, crossingBeaconSeats } from './sprites';
import { RATE, SurgeAnimator } from './surge';
import { clear, el } from './svg';
import {
  attachPoint,
  deadendSide,
  spanConductors,
  towerFacings,
  wireArcLength,
  wirePath,
} from './wires';

export interface RenderResult {
  net: Network;
  /** How long the surge will take to finish, in ms. */
  maxDelay: number;
}

/** What a render should animate beyond the diff — one-shot, from the tap site. */
export interface RenderOptions {
  /** The tower this tap raised: it settles. Restores and re-renders never replay it. */
  justBuilt?: Point;
  /** The tower this tap removed: its sprite is lifted out instead of vanishing. */
  justRemoved?: Point;
}

/**
 * The board's tempos (STYLEGUIDE §9: one clock). Each is defined here once
 * and published to the stylesheet in mount(), so the CSS animation and the
 * timer that cleans up after it cannot drift apart.
 */
const RANGE_PULSE_MS = 900;
const DENY_PULSE_MS = 200;

/**
 * How far the drawn world extends past the surveyed plot, in board units.
 * The frame crops the world, not the plot: a corridor continues past the
 * survey line and an edge house has ground behind it. Sized from the sprite
 * drawings — the suspension footing reaches 2 units below a row-8 cell, the
 * widest arms 43 to a side — with a little air past each.
 *
 * The top is deliberately zero: the world ends at the readout's rule, and a
 * row-0 mast rises past it instead — the svg overflows visibly, and the hud
 * stacks above the board (base.css) so a mast climbs the band's padding but
 * never covers a reading. Towers standing in front of the operator's
 * readout is depth the map earns for free; a sky band that pushed the strip
 * away would just be mat.
 */
const WORLD: ShoreMargins = { top: 0, right: 8, bottom: 16, left: 8 };

/**
 * How long a touch has to rest on a cell before it counts as a look rather
 * than a tap. Long enough not to fire during a normal press, short enough
 * that the free look feels free.
 */
const LONG_PRESS_MS = 350;

/** Declared as properties, not methods, so they can be safely destructured. */
export interface BoardCallbacks {
  /** A cell was tapped. Blocked cells report too — the UI explains why. */
  onCell: (p: Point) => void;
  /**
   * Power reached a tower, mid-surge. `order` counts them in arrival order so
   * the sound can climb as the line lights up.
   */
  onEnergized?: (order: number) => void;
  /** A house got its lights, mid-surge. */
  onHouseLit?: (order: number) => void;
}

/**
 * Draws the board and turns taps into game events.
 *
 * Holds one piece of state of its own: which wires, towers and houses were
 * live on the previous frame. That's what separates "already lit, draw it
 * lit" from "just lit, animate the surge into it".
 */
export class BoardRenderer {
  private readonly svg: SVGSVGElement;
  private readonly hoverable = canHover();
  private readonly surge: SurgeAnimator;

  /** Live, not cached: the settings switch can flip mid-session. */
  private get reduceMotion(): boolean {
    return prefersReducedMotion();
  }

  private gTerrain!: SVGGElement;
  private gFx!: SVGGElement;
  private gReference!: SVGGElement;
  private gHover!: SVGGElement;
  private gSprites!: SVGGElement;
  private gSparks!: SVGGElement;
  private gSmoke!: SVGGElement;
  private gPlantSmoke!: SVGGElement;
  private gInhabitants!: SVGGElement;
  private gHits!: SVGGElement;
  private gCursor!: SVGGElement;

  private prevLiveWires = new Set<string>();
  private prevLiveTowers = new Set<string>();
  private prevLitHouses = new Set<string>();

  /** Smoke sprite per house, keyed by cell — persistent so SMIL doesn't
   * restart every tap; class `.lit` gates visibility. */
  private smokeByHouse = new Map<string, SVGGElement>();

  /** One smoke plume per plant stack — persistent, opacity driven by the
   *  MW the grid is currently delivering. */
  private plantSmokeGroups: SVGGElement[] = [];

  /** Ambient inhabitants (birds, hawk). Rebuilt on each mount. */
  private inhabitants: Inhabitants | null = null;

  private puzzle: Puzzle | null = null;
  private callbacks: BoardCallbacks | null = null;

  /** Cells no tower can stand on — terrain, the plant, the houses. */
  private blockedCells = new Set<string>();
  /** Where the keyboard survey cursor sits, or `null` while it's hidden. */
  private cursor: Point | null = null;
  /** A long press opened a preview, so the click that follows it isn't a tap. */
  private previewing = false;

  constructor(svg: SVGSVGElement) {
    this.svg = svg;
    this.surge = new SurgeAnimator(
      () => this.gSparks,
      () => this.reduceMotion,
    );
    this.bindKeyboard();
    // Where the long press is the free look, the system's own long-press
    // menu is in the way of it.
    if (!this.hoverable) {
      this.svg.addEventListener('contextmenu', (e) => e.preventDefault());
    }
  }

  /** Tear down the old board and lay out a new grid. */
  mount(puzzle: Puzzle, callbacks: BoardCallbacks): void {
    // Retire the old grid's inhabitants before the tree goes. clear() alone
    // detaches the hawk without cancelling its infinite orbit animation, and
    // a running animation on a detached node holds the whole document on a
    // per-frame main-thread pass for the rest of the session.
    this.inhabitants?.dispose();
    this.puzzle = puzzle;
    this.callbacks = callbacks;
    this.surge.clear();
    this.resetSurgeMemory();

    // The visible map is the plot plus the world margin on every side (see
    // WORLD above); cells, sprites and hit targets keep plot coordinates.
    this.svg.setAttribute(
      'viewBox',
      `${-WORLD.left} ${-WORLD.top} ${GRID_SIZE * CELL + WORLD.left + WORLD.right} ${GRID_SIZE * CELL + WORLD.top + WORLD.bottom}`,
    );

    // Publish the tempos on the document root: the HTML HUD left the board's
    // wrap for its own card (STYLEGUIDE §7), so the wrap's scope no longer
    // reaches it — the root reaches both it and the SVG.
    const host = this.svg.ownerDocument.documentElement;
    host.style.setProperty('--cell-ms', `${RATE * CELL}ms`);
    host.style.setProperty('--range-pulse', `${RANGE_PULSE_MS}ms`);
    host.style.setProperty('--deny-pulse', `${DENY_PULSE_MS}ms`);

    clear(this.svg);
    buildDefs(this.svg, !this.reduceMotion);

    this.gTerrain = el('g', { id: 'terrain' }, this.svg);
    this.gFx = el('g', { id: 'fx' }, this.svg);
    // The engineers' drawing sits under the player's own wires, so a live
    // circuit always reads on top of a plan (STYLEGUIDE §5).
    this.gReference = el('g', { id: 'reference' }, this.svg);
    this.gHover = el('g', { id: 'hover' }, this.svg);
    // Wires and sprites interleave in one painter's ordering inside
    // #sprites (render() sorts them together by row), so a span reads in
    // front of structures behind it and tucks behind structures nearer
    // than it. Sparks ride above the whole scene: the travelling surge
    // never disappears behind a building.
    this.gSprites = el('g', { id: 'sprites' }, this.svg);
    this.gSparks = el('g', { id: 'sparks' }, this.svg);
    // Ambient layers sit above the placed objects, below hit targets. Smoke
    // and inhabitants each own a persistent group so their SMIL loops don't
    // restart on every tap (STYLEGUIDE §8 item 6).
    this.gSmoke = el('g', { id: 'smoke' }, this.svg);
    this.gPlantSmoke = el('g', { id: 'plant-smoke' }, this.svg);
    this.gInhabitants = el('g', { id: 'inhabitants' }, this.svg);
    this.gHits = el('g', { id: 'hits' }, this.svg);
    this.gCursor = el('g', { id: 'cursor' }, this.svg);

    this.cursor = null;
    this.smokeByHouse.clear();
    this.drawTerrain(puzzle);
    this.buildHouseSmoke(puzzle);
    this.buildPlantSmoke(puzzle);
    this.buildHits(puzzle);
    this.inhabitants = new Inhabitants(this.gInhabitants, puzzle);
  }

  /**
   * Re-issue the defs after a motion-setting change. The board's
   * free-running animations — the crossing giant's beacon, water drift, the
   * per-tile water glints, the chimney smoke curls, and the birds/hawk — are
   * all SMIL loops out of reach of the stylesheet flag. Rebuild every SMIL
   * host so the flag change actually takes.
   */
  refreshMotion(): void {
    if (!this.puzzle) return;
    this.svg.querySelector('defs')?.remove();
    buildDefs(this.svg, !this.reduceMotion);
    this.drawTerrain(this.puzzle);
    this.buildHouseSmoke(this.puzzle);
    this.buildPlantSmoke(this.puzzle);
    this.inhabitants?.dispose();
    this.inhabitants = new Inhabitants(this.gInhabitants, this.puzzle);
    clear(this.gInhabitants);
  }

  /** Forget what was lit, so the next frame replays the surge from scratch. */
  private resetSurgeMemory(): void {
    this.prevLiveWires = new Set();
    this.prevLiveTowers = new Set();
    this.prevLitHouses = new Set();
  }

  /** Cancel any in-flight surge. */
  cancelPending(): void {
    this.surge.clear();
  }

  /** Schedule work on the surge's timeline, so a later tap cancels it too. */
  schedule(fn: () => void, ms: number): void {
    this.surge.later(fn, ms);
  }

  private drawTerrain(puzzle: Puzzle): void {
    clear(this.gTerrain);
    const size = GRID_SIZE * CELL;
    // a single flat land field under a hairline survey grid — a map, not a
    // checkerboard — covering the world margin too: the land doesn't stop
    // where the survey does
    el(
      'rect',
      {
        x: -WORLD.left,
        y: -WORLD.top,
        width: size + WORLD.left + WORLD.right,
        height: size + WORLD.top + WORLD.bottom,
        fill: 'var(--land)',
      },
      this.gTerrain,
    );
    // The land-tone patchwork (STYLEGUIDE §4): a seeded third of the land
    // cells carry a breath of tint, one path union under the hairlines so
    // the seams sit beneath the survey grid. Independent per-cell draws
    // cannot alternate, so the checkerboard ban holds — this is the "maps
    // have drift" license, not a pattern. Obstacle cells are skipped:
    // water is opaquely covered anyway, and the tint must never modulate
    // the translucent blocked-terrain fill under a peak.
    // An edge cell's tone bleeds across the plot line to the crop, the way
    // the water contour does: the margin is more of the same ground, and a
    // patchwork that stopped at the survey line would read as a mat.
    const tone: string[] = [];
    for (let r = 0; r < GRID_SIZE; r++) {
      for (let c = 0; c < GRID_SIZE; c++) {
        if (puzzle.obstacles[key({ r, c })]) continue;
        const rnd = mulberry32(dayNumber() ^ LAND_TONE_SALT ^ (r * 97 + c * 31));
        if (rnd() >= 0.33) continue;
        const x0 = c === 0 ? -WORLD.left : c * CELL;
        const x1 = c === GRID_SIZE - 1 ? size + WORLD.right : (c + 1) * CELL;
        const y0 = r === 0 ? -WORLD.top : r * CELL;
        const y1 = r === GRID_SIZE - 1 ? size + WORLD.bottom : (r + 1) * CELL;
        tone.push(`M${x0} ${y0}H${x1}V${y1}H${x0}Z`);
      }
    }
    if (tone.length) {
      el('path', { class: 'land-tone', d: tone.join(''), fill: 'var(--land-tone)' }, this.gTerrain);
    }
    for (let i = 1; i < GRID_SIZE; i++) {
      el(
        'path',
        {
          class: 'survey-grid',
          d: `M${i * CELL} 0 V${size} M0 ${i * CELL} H${size}`,
          stroke: 'var(--grid-line)',
          'stroke-width': 1,
        },
        this.gTerrain,
      );
    }
    // No line marks the plot's outer boundary: the hairlines simply end,
    // and the margin reads as the same ground running under the frame — a
    // drawn box here turned the world margin into a mat. Where the plot
    // ends is already told by the grid stopping, and told exactly by the
    // survey marker the moment a finger or cursor asks.
    // The water, drawn once from its contour (shore.ts): the same closed
    // path is the fill here and the shoreline stroke below, so the edge and
    // the body can never disagree — coves, bays, wobble and all.
    const shore = shorelinePath(puzzle.obstacles, size, WORLD);
    if (shore) {
      el('path', { d: shore, fill: 'var(--water)' }, this.gTerrain);
      // The shallows: a thin lighter band along the water's own side of the
      // bank — the cartographer's inner coastline. The widened contour
      // stroke straddles the boundary, and clipping it to the fill keeps
      // only the inner half. Decoration, so it wears the shoreline's class
      // and leaves with it under the accessibility modes.
      const clip = el('clipPath', { id: 'shoreClip' }, this.gTerrain);
      el('path', { d: shore }, clip);
      el(
        'path',
        {
          class: 'shoreline',
          d: shore,
          fill: 'none',
          stroke: 'var(--ripple)',
          'stroke-width': 10,
          opacity: 0.3,
          'clip-path': 'url(#shoreClip)',
        },
        this.gTerrain,
      );
    }
    // Everything wet — ripples and glints — draws inside the water's own
    // clip, so no stroke lands on the bank however far the wobble reaches.
    const gWater = el('g', shore ? { 'clip-path': 'url(#shoreClip)' } : {}, this.gTerrain);
    for (const [k, t] of Object.entries(puzzle.obstacles)) {
      const p = parseKey(k);
      // each tile picks one of the drawn variants, so terrain drifts like a map
      const variant = (p.r * 7 + p.c * 13) % TERRAIN_VARIANTS;
      if (t !== 'water') {
        el('use', { href: SPRITE.mtn + variant, x: cx(p), y: cy(p) }, this.gTerrain);
        continue;
      }
      // Ripples keep to the banks (§4): a tile's strokes pull toward its
      // land neighbours and jitter in size, and open water goes sparser and
      // smaller — busy where the water meets the ground, calm in the
      // middle, matching the shallows band. Rolls draw in a fixed order
      // (the flora discipline), so an edit here never reshuffles the field.
      const rnd = mulberry32(dayNumber() ^ WATER_RIPPLE_SALT ^ (p.r * 97 + p.c * 31));
      const scale = 0.85 + rnd() * 0.3;
      const calm = rnd();
      const bank = { x: 0, y: 0 };
      let banks = 0;
      for (const [dr, dc] of [
        [-1, 0],
        [1, 0],
        [0, -1],
        [0, 1],
      ] as const) {
        const r = p.r + dr;
        const c = p.c + dc;
        if (r < 0 || r >= GRID_SIZE || c < 0 || c >= GRID_SIZE) continue;
        if (puzzle.obstacles[key({ r, c })] !== 'water') {
          bank.x += dc;
          bank.y += dr;
          banks++;
        }
      }
      // Ambient water glint — a short brightening on a long cycle, per-tile
      // seeded so tiles never all glint at once. (§8 item 6.)
      if (!this.reduceMotion) this.addWaterGlint(p, gWater);
      // Half the open-water tiles carry no ripples at all.
      if (banks === 0 && calm < 0.5) continue;
      const mag = Math.hypot(bank.x, bank.y);
      const pull = 13;
      const ox = mag ? (bank.x / mag) * pull : 0;
      const oy = mag ? (bank.y / mag) * pull : 0;
      const s = banks === 0 ? scale * 0.75 : scale;
      el(
        'use',
        {
          href: SPRITE.water + variant,
          transform:
            `translate(${+(cx(p) + ox).toFixed(1)} ${+(cy(p) + oy).toFixed(1)})` +
            ` scale(${+s.toFixed(2)})`,
        },
        gWater,
      );
    }
    // The shoreline edge (STYLEGUIDE §4): one stroke along the whole
    // water/land boundary, over the ripples and before the flora so the
    // reeds straddle it — the water contour again, this time as a line.
    if (shore) {
      el(
        'path',
        {
          class: 'shoreline',
          d: shore,
          fill: 'none',
          stroke: 'var(--shore)',
          'stroke-width': 1,
          'stroke-linecap': 'round',
          'stroke-linejoin': 'round',
          'vector-effect': 'non-scaling-stroke',
        },
        this.gTerrain,
      );
    }
    // Ground flora goes on after the shoreline arcs, so a bay never slices a
    // tuft and the water's-edge reeds stand over the arc, straddling the
    // shore (STYLEGUIDE §4 — terrain art, not ambient life).
    drawFlora(this.gTerrain, puzzle);
    // The operator's-hour wash, over the whole map (STYLEGUIDE §1). The map
    // fills its frame now, so the tint rides the terrain itself as one
    // near-transparent rect — decoration, hidden under both accessibility
    // modes with the rest of the ambient layer.
    el(
      'rect',
      {
        class: 'hour-tint',
        x: -WORLD.left,
        y: -WORLD.top,
        width: size + WORLD.left + WORLD.right,
        height: size + WORLD.top + WORLD.bottom,
        fill: 'var(--hour-tint, transparent)',
        'fill-opacity': 0.08,
        'pointer-events': 'none',
      },
      this.gTerrain,
    );
  }

  private addWaterGlint(p: Point, parent: SVGGElement): void {
    const rnd = mulberry32(dayNumber() ^ WATER_GLINT_SALT ^ (p.r * 97 + p.c * 31));
    const gx = -22 + Math.floor(rnd() * 44);
    const gy = -22 + Math.floor(rnd() * 44);
    const beginS = rnd() * 12;
    const glint = el(
      'path',
      {
        class: 'water-glint',
        d: `M${cx(p) + gx - 4} ${cy(p) + gy} h8`,
        stroke: 'var(--matte)',
        'stroke-width': 1,
        'stroke-linecap': 'round',
        opacity: 0,
      },
      parent,
    );
    // 12s cycle, spike near the end so the tile is quiet most of the time.
    // A composited opacity animation rather than a SMIL tween, for the same
    // reason the smoke is one (board.css); the seeded phase becomes a
    // negative delay, so the loop is already under way on the first frame.
    glint.style.cssText = `--glint-delay:-${beginS.toFixed(2)}s`;
  }

  private buildHouseSmoke(puzzle: Puzzle): void {
    clear(this.gSmoke);
    this.smokeByHouse.clear();
    puzzle.houses.forEach((h, i) => {
      // Chimney anchor in house-local coords is roughly the top-center of the
      // chimney rect drawn in sprites.ts: (13.5, -16) offset from house
      // origin, multiplied by SPRITE_SCALE because the house def is wrapped
      // in a scale(SPRITE_SCALE) group.
      const ax = cx(h) + 13.5 * SPRITE_SCALE;
      const ay = cy(h) - 16 * SPRITE_SCALE;
      const g = el('g', { class: 'house-smoke', transform: `translate(${ax} ${ay})` }, this.gSmoke);
      // Three staggered puffs so the plume reads as continuous. Each rides
      // one composited CSS animation (`smoke-puff` in board.css) rather than
      // three SMIL attribute tweens; the parent group's `.lit` class gates
      // whether we see the whole thing at all, and pauses the cycle when we
      // do not. The phase is a negative delay, so a plume is already mid-
      // flight on the first frame instead of taking a full cycle to build.
      if (this.reduceMotion) {
        // Static wisp when motion is off — visible presence, no rise.
        el('circle', { cx: 0, cy: -12, r: 4, fill: 'var(--smoke, #b9c2d4)', opacity: 0.3 }, g);
      } else {
        for (let n = 0; n < 3; n++) {
          const base = (n - 1) * 1.5;
          const c = el(
            'circle',
            {
              cx: base,
              cy: 0,
              // Bigger, gently growing as the puff rises so the plume
              // dissipates. Sized to read at phone scale (~2–3 CSS px each).
              r: 3.2 + n * 0.6,
              fill: 'var(--smoke, #b9c2d4)',
              opacity: 0,
            },
            g,
          );
          const phase = (n * 3.4 + ((i * 0.7) % 3)).toFixed(2);
          c.style.cssText =
            `--puff-rise:-34px;--puff-curl:3px;--puff-drift:-1px;` +
            `--puff-peak:0.4;--puff-mid:0.32;--puff-dur:10s;--puff-delay:-${phase}s`;
        }
      }
      this.smokeByHouse.set(key(h), g);
    });
  }

  /**
   * Two smoke plumes on the plant, one per stack, persistent across renders
   * so the SMIL puffs never restart mid-flight. Opacity is driven from
   * setPlantOutput() so the visible density scales with the MW the grid is
   * currently delivering — a dark grid stops the plume, and a well-tuned
   * one has it churning at full weight.
   */
  private buildPlantSmoke(puzzle: Puzzle): void {
    clear(this.gPlantSmoke);
    this.plantSmokeGroups = [];
    // Stack anchors in sprite-local coords come from sprites.ts (plant
    // stacks at x=-20..-11,y=-20..-2 and x=-7..+2,y=-14..-2 — top-centres
    // at (-15.5,-20) and (-2.5,-14)). SPRITE_SCALE wraps the plant, so
    // rendered anchors are those coords × SPRITE_SCALE.
    const S = SPRITE_SCALE;
    const stacks: { dx: number; dy: number; scale: number }[] = [
      { dx: -15.5 * S, dy: -20 * S, scale: 1.2 }, // tall stack — bigger plume
      { dx: -2.5 * S, dy: -14 * S, scale: 0.9 }, // short stack — a smaller one
    ];
    for (let i = 0; i < stacks.length; i++) {
      const s = stacks[i];
      const ax = cx(puzzle.plant) + s.dx;
      const ay = cy(puzzle.plant) + s.dy;
      const g = el(
        'g',
        {
          class: 'plant-smoke',
          transform: `translate(${ax} ${ay}) scale(${s.scale})`,
        },
        this.gPlantSmoke,
      );
      if (this.reduceMotion) {
        el('circle', { cx: 0, cy: -14, r: 5, fill: 'var(--smoke, #b9c2d4)', opacity: 0.3 }, g);
      } else {
        // Four staggered puffs so the plume reads as continuous and
        // industrial — a working plant, not a candle. One composited CSS
        // animation each, as with the chimneys.
        for (let n = 0; n < 4; n++) {
          const c = el(
            'circle',
            {
              cx: (n - 1.5) * 1.5,
              cy: 0,
              r: 3.6 + n * 0.7,
              fill: 'var(--smoke, #b9c2d4)',
              opacity: 0,
            },
            g,
          );
          const phase = (n * 2 + ((i * 0.9) % 2)).toFixed(2);
          c.style.cssText =
            `--puff-rise:-52px;--puff-curl:4px;--puff-drift:-2px;` +
            `--puff-peak:0.55;--puff-mid:0.35;--puff-dur:8s;--puff-delay:-${phase}s`;
        }
      }
      this.plantSmokeGroups.push(g);
    }
  }

  /**
   * Set the plume density on both plant stacks from the megawatts the grid
   * is currently delivering. Zero MW stops the plume entirely (dark grid,
   * no combustion); higher MW pushes both plumes toward full opacity. The
   * curve is roughly linear against a 100 MW ceiling — enough range that
   * a middling grid reads as visibly cooler than a well-tuned one.
   */
  setPlantOutput(mw: number | null): void {
    const intensity = mw == null || mw <= 0 ? 0 : Math.max(0, Math.min(1, mw / 100));
    for (const g of this.plantSmokeGroups) {
      g.style.opacity = String(intensity);
      // `.lit` is what stops the puff cycle on a dark plant. Without it the
      // animation keeps ticking under a fully transparent group, which is a
      // frame spent on something nobody can see (§8.6).
      g.classList.toggle('lit', intensity > 0);
    }
  }

  private buildHits(puzzle: Puzzle): void {
    clear(this.gHits);
    this.blockedCells = new Set();
    for (let r = 0; r < GRID_SIZE; r++) {
      for (let c = 0; c < GRID_SIZE; c++) {
        const p = { r, c };
        const k = key(p);
        const blocked =
          Boolean(puzzle.obstacles[k]) ||
          k === key(puzzle.plant) ||
          puzzle.houses.some((h) => key(h) === k);
        if (blocked) this.blockedCells.add(k);
        const hit = el(
          'rect',
          {
            x: c * CELL,
            y: r * CELL,
            width: CELL,
            height: CELL,
            class: 'cell-hit' + (blocked ? ' blocked' : ''),
          },
          this.gHits,
        );
        hit.addEventListener('click', () => {
          // A press that opened a preview is spent on the preview.
          if (this.previewing) {
            this.previewing = false;
            return;
          }
          this.callbacks?.onCell(p);
        });
        if (blocked) continue;
        if (this.hoverable) {
          hit.addEventListener('pointerenter', () => this.onHoverEnter?.(p));
          hit.addEventListener('pointerleave', () => this.clearGhost());
        } else {
          this.bindLongPress(hit, p);
        }
      }
    }
  }

  /**
   * Touch has no hover, so the free look is a long press: hold a buildable
   * cell to see the marker, the span and the price, then lift without
   * building. Pillar 3 puts the cost of a plan at zero, and without this it
   * costs two taps on a phone — which is the tax the retired move counter
   * used to charge for exactly the same thing (GDD §9).
   */
  private bindLongPress(hit: SVGElement, p: Point): void {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const cancel = (): void => {
      if (timer) clearTimeout(timer);
      timer = null;
    };

    hit.addEventListener('pointerdown', () => {
      // Each gesture starts clean, which also clears a flag stranded by a
      // press that wandered off the cell and never became a click.
      this.previewing = false;
      cancel();
      timer = setTimeout(() => {
        timer = null;
        // Clear first, so what is counted is what THIS press drew. A ghost
        // left behind by the keyboard survey cursor would otherwise look like
        // a preview and swallow a tap the controller never previewed.
        this.clearGhost();
        this.onHoverEnter?.(p);
        // Only a preview that actually drew swallows the tap: on a cell the
        // controller declined to ghost, the press stays an ordinary tap.
        this.previewing = this.gHover.childElementCount > 0;
      }, LONG_PRESS_MS);
    });

    /**
     * `expectClick` is the whole subtlety: a press lifted on the cell is
     * followed by a click that has to be swallowed, so the flag must outlive
     * it. A press that is cancelled or wanders off produces no click at all,
     * so leaving the flag set would swallow the *next* tap instead — including
     * a tap on blocked terrain, which has a click handler but no pointerdown
     * to reset it.
     */
    const release = (expectClick: boolean): void => {
      cancel();
      if (!this.previewing) return;
      this.clearGhost();
      if (!expectClick) this.previewing = false;
    };
    hit.addEventListener('pointerup', () => release(true));
    hit.addEventListener('pointercancel', () => release(false));
    hit.addEventListener('pointerleave', () => release(false));
  }

  /** Set by the controller: decides whether a ghost tower should appear. */
  onHoverEnter: ((p: Point) => void) | null = null;

  /**
   * A thin dashed survey marker on a valid placement target — not a fill, not
   * a glow — with the span it would cover. Where a tower would stand and what
   * it would reach, never what it would score: a projected MW was cut for
   * turning the spatial question into cursor-scrubbing (GDD §9).
   */
  showGhost(p: Point): void {
    clear(this.gHover);
    el(
      'rect',
      {
        x: p.c * CELL + 6,
        y: p.r * CELL + 6,
        width: CELL - 12,
        height: CELL - 12,
        rx: 2,
        class: 'survey-marker',
      },
      this.gHover,
    );
    // The span this tower would cover — the pulse's square, quieter and
    // static: a plan costs nothing, so it shows before the tap.
    el('rect', { ...this.spanBox(p), rx: 2, class: 'span-preview' }, this.gHover);
  }

  clearGhost(): void {
    clear(this.gHover);
  }

  /**
   * Lay the engineers' drawing on the board: the reference layout as a
   * dashed steel diagram with open squares for its towers.
   *
   * A drawing, not a circuit — no glow, no surge, no sound (STYLEGUIDE §5).
   * The player's own line stays the only live thing on the board, and this
   * only ever appears on a grid whose race is over and which they finished
   * (GDD §4.5).
   */
  showReference(towers: Point[]): void {
    const puzzle = this.puzzle;
    if (!puzzle) return;
    clear(this.gReference);

    const net = network(puzzle, towers);
    const roles = towerRoles(puzzle, net);

    for (const e of net.edges) {
      const d = wirePath(
        attachPoint(e.a, roles[e.ai], cx(e.b)),
        attachPoint(e.b, roles[e.bi], cx(e.a)),
      );
      el('path', { d, class: 'ref-wire' }, this.gReference);
    }
    for (const hl of net.houseLinks) {
      if (!hl.node || hl.nodeIdx === null) continue;
      const d = wirePath(
        attachPoint(hl.node, roles[hl.nodeIdx], cx(hl.h)),
        attachPoint(hl.h, 'house', cx(hl.node)),
      );
      el('path', { d, class: 'ref-wire' }, this.gReference);
    }
    for (const t of towers) {
      // A survey square framing the whole cell, not a chip behind the sprite:
      // where the player's own tower stands on the same spot — the perfect
      // grid's whole point — a small mark vanishes under it. Framing the cell
      // keeps the engineers' stake legible either way, in the same dashed
      // idiom as the placement marker (§4), a step inside it so the two never
      // read as one square.
      el(
        'rect',
        {
          x: t.c * CELL + 9,
          y: t.r * CELL + 9,
          width: CELL - 18,
          height: CELL - 18,
          rx: 2,
          class: 'ref-node',
        },
        this.gReference,
      );
    }
  }

  clearReference(): void {
    // Tolerates being called before the first mount: starting a session clears
    // the drawing, and the first session starts before there is a board.
    if (this.gReference) clear(this.gReference);
  }

  /** Is the reference drawing currently on the board? */
  get showingReference(): boolean {
    return (this.gReference?.childElementCount ?? 0) > 0;
  }

  /** The square of cells within span of `p`, clipped to the board. */
  private spanBox(p: Point): { x: number; y: number; width: number; height: number } {
    const x0 = Math.max(0, p.c - SPAN) * CELL;
    const y0 = Math.max(0, p.r - SPAN) * CELL;
    const x1 = Math.min(GRID_SIZE, p.c + SPAN + 1) * CELL;
    const y1 = Math.min(GRID_SIZE, p.r + SPAN + 1) * CELL;
    return { x: x0 + 3, y: y0 + 3, width: x1 - x0 - 6, height: y1 - y0 - 6 };
  }

  /** One fading ping showing a new tower's reach. */
  pulseRange(p: Point): void {
    if (this.reduceMotion) return;
    const rect = el('rect', { ...this.spanBox(p), rx: 2, class: 'range-pulse' }, this.gFx);
    // +50: removal waits out the fade rather than racing it.
    setTimeout(() => rect.remove(), RANGE_PULSE_MS + 50);
  }

  /** One brief steel pulse on a refused cell — the hum's visual half. */
  pulseDenied(p: Point): void {
    if (this.reduceMotion) return;
    const rect = el(
      'rect',
      {
        x: p.c * CELL + 2,
        y: p.r * CELL + 2,
        width: CELL - 4,
        height: CELL - 4,
        rx: 2,
        class: 'deny-pulse',
      },
      this.gFx,
    );
    setTimeout(() => rect.remove(), DENY_PULSE_MS + 50);
  }

  // ---------- keyboard ----------

  /**
   * The board plays from the keyboard too: arrows move a survey cursor, Enter
   * or Space taps the cell under it. Same verb, same rules — the cursor is an
   * input method, not a second mechanic (GDD §3.1).
   */
  private bindKeyboard(): void {
    // Every handler waits for mount() — before it there are no groups to draw
    // into and no grid to move around.
    this.svg.addEventListener('keydown', (e) => {
      if (this.puzzle) this.onKey(e);
    });
    // Reveal the cursor for keyboard focus only — a click focuses the board
    // too, and a pointer player shouldn't grow an amber square.
    this.svg.addEventListener('focus', () => {
      if (this.puzzle && this.svg.matches(':focus-visible')) {
        this.moveCursor(this.cursor ?? this.centre());
      }
    });
    this.svg.addEventListener('blur', () => {
      if (this.puzzle) this.hideCursor();
    });
  }

  private centre(): Point {
    return { r: Math.floor(GRID_SIZE / 2), c: Math.floor(GRID_SIZE / 2) };
  }

  private onKey(e: KeyboardEvent): void {
    const dir: Record<string, [number, number]> = {
      ArrowUp: [-1, 0],
      ArrowDown: [1, 0],
      ArrowLeft: [0, -1],
      ArrowRight: [0, 1],
    };
    if (e.key in dir) {
      e.preventDefault();
      const [dr, dc] = dir[e.key];
      const from = this.cursor;
      // The first arrow press reveals the cursor where it last was; only a
      // visible cursor moves.
      const to = from
        ? {
            r: Math.min(GRID_SIZE - 1, Math.max(0, from.r + dr)),
            c: Math.min(GRID_SIZE - 1, Math.max(0, from.c + dc)),
          }
        : this.centre();
      this.moveCursor(to);
    } else if ((e.key === 'Enter' || e.key === ' ') && this.cursor) {
      e.preventDefault();
      this.callbacks?.onCell(this.cursor);
      this.previewAt(this.cursor);
    } else if (e.key === 'Escape' && this.cursor) {
      this.hideCursor();
    }
  }

  private moveCursor(p: Point): void {
    this.cursor = p;
    clear(this.gCursor);
    el(
      'rect',
      {
        x: p.c * CELL + 2,
        y: p.r * CELL + 2,
        width: CELL - 4,
        height: CELL - 4,
        class: 'key-cursor',
      },
      this.gCursor,
    );
    this.previewAt(p);
  }

  /** Same plan preview the pointer gets: ghost and span, valid cells only. */
  private previewAt(p: Point): void {
    this.clearGhost();
    if (!this.blockedCells.has(key(p))) this.onHoverEnter?.(p);
  }

  private hideCursor(): void {
    this.cursor = null;
    clear(this.gCursor);
    this.clearGhost();
  }

  /**
   * Sound the surge in the order power actually arrives, so a long line reads
   * as a rising run rather than a cluster of identical blips. When motion is
   * reduced the visuals are instant, so the audio compresses with them: the
   * run's destination rung once, one bell above it — never the whole run as a
   * chord (STYLEGUIDE §9).
   */
  private scheduleSurgeSounds(towers: number[], houses: number[]): void {
    const { onEnergized, onHouseLit } = this.callbacks ?? {};
    const lit = houses.filter((d) => Number.isFinite(d));
    if (this.reduceMotion) {
      if (towers.length && onEnergized) onEnergized(towers.length - 1);
      if (lit.length && onHouseLit) onHouseLit(Math.max(0, towers.length - 1));
      return;
    }
    if (onEnergized) {
      towers
        .sort((a, b) => a - b)
        .forEach((delay, i) => this.surge.later(() => onEnergized(i), delay));
    }
    if (onHouseLit) {
      lit.sort((a, b) => a - b).forEach((delay, i) => this.surge.later(() => onHouseLit(i), delay));
    }
  }

  /** Redraw wires and sprites for the current layout, animating anything newly live. */
  render(towers: Point[], opts: RenderOptions = {}): RenderResult {
    const puzzle = this.puzzle;
    if (!puzzle) throw new Error('BoardRenderer.render() before mount()');

    this.surge.clear();
    this.retireTower(opts.justRemoved);
    clear(this.gSparks);
    clear(this.gSprites);

    const net = network(puzzle, towers);
    const roles = towerRoles(puzzle, net);
    // which view each tower wears: a tower faces its line, so mostly
    // east-west spans turn it to the profile drawing (STYLEGUIDE §5)
    const facings = towerFacings(net);
    let maxDelay = 0;

    const liveWireKeys = new Set<string>();
    const liveTowerKeys = new Set<string>();
    const litHouseKeys = new Set<string>();

    // Which faces of each dead-end anchor hold wire this frame. Terminal
    // hardware rides per-side overlays, so an unused face stays bare.
    const deadendSides = new Map<number, Set<'left' | 'right'>>();
    const noteSide = (i: number, n: Point, towardX: number) => {
      if (roles[i] !== 'deadend') return;
      let sides = deadendSides.get(i);
      if (!sides) deadendSides.set(i, (sides = new Set()));
      sides.add(deadendSide(n, towardX));
    };

    // Wires and sprites share one painter's list, drawn back-to-front by
    // row so nearer things overlap farther ones. A span's depth is its
    // nearer (lower) endpoint's row, and the tie goes to sprites: a wire
    // tucks behind the structures of its own row but rides in front of
    // everything farther back — a span never vanishes behind a building
    // it passes over.
    const drawables: { r: number; kind: 0 | 1; make: () => void }[] = [];

    for (const e of net.edges) {
      // three phase wires per span, terminals matched for shortest total
      // wire — the pairing under which no two conductors cross
      const pairs = spanConductors(
        e.a,
        roles[e.ai],
        facings[e.ai],
        e.b,
        roles[e.bi],
        facings[e.bi],
      );
      noteSide(e.ai, e.a, cx(e.b));
      noteSide(e.bi, e.b, cx(e.a));
      // one sag for the whole bundle — e.len is the span's own chord, so the
      // three conductors hang as one unit instead of crossing mid-span
      const ds = pairs.map(([p, q]) => wirePath(p, q, e.len));
      const wk = key(e.a) + '|' + key(e.b);
      if (e.live) liveWireKeys.add(wk);
      const isNew = e.live && !this.prevLiveWires.has(wk);
      const cls = (live: boolean, i: number) =>
        (live ? 'wire-live' : 'wire-dead') + (i === 1 ? '' : ' wire-outer');
      const from = net.dist[e.ai] <= net.dist[e.bi] ? e.ai : e.bi;
      const delay = net.dist[from] * RATE;
      if (isNew) maxDelay = Math.max(maxDelay, delay + e.len * RATE);
      drawables.push({
        r: Math.max(e.a.r, e.b.r),
        kind: 0,
        make: () => {
          const paths = ds.map((d, i) =>
            el('path', { d, class: cls(e.live && !isNew, i) }, this.gSprites),
          );
          // Phase-lock a dead bundle's dash pattern at mid-span, where the
          // conductors run closest — three free-running dashes of slightly
          // different arc length beat against each other. Inert once live
          // (no dasharray there), so the attribute rides through the swap.
          const mid = wireArcLength(pairs[1][0], pairs[1][1], e.len);
          paths.forEach((p, i) => {
            if (i === 1) return;
            const l = wireArcLength(pairs[i][0], pairs[i][1], e.len);
            p.setAttribute('stroke-dashoffset', (((l - mid) / 2) % 15).toFixed(2));
          });
          if (isNew) {
            this.surge.later(
              () => paths.forEach((p, i) => p.setAttribute('class', cls(true, i))),
              delay,
            );
            this.surge.launchSpark(ds[1], e.len, from === e.bi, delay);
          }
        },
      });
    }

    for (const hl of net.houseLinks) {
      if (!hl.node || hl.nodeIdx === null) continue;
      const node = hl.node;
      const nodeIdx = hl.nodeIdx;
      const d = wirePath(
        attachPoint(node, roles[nodeIdx], cx(hl.h), facings[nodeIdx]),
        attachPoint(hl.h, 'house', cx(node)),
      );
      const hk = 'h' + key(hl.h);
      if (hl.lit) litHouseKeys.add(hk);
      const isNew = hl.lit && !this.prevLitHouses.has(hk);
      const delay = net.dist[nodeIdx] * RATE;
      if (isNew) maxDelay = Math.max(maxDelay, hl.arrive * RATE);
      drawables.push({
        r: Math.max(node.r, hl.h.r),
        kind: 0,
        make: () => {
          const path = el(
            'path',
            { d, class: hl.lit && !isNew ? 'wire-live' : 'wire-dead' },
            this.gSprites,
          );
          if (isNew) {
            this.surge.later(() => path.setAttribute('class', 'wire-live'), delay);
            this.surge.launchSpark(d, hl.wireLen, false, delay);
          }
        },
      });
    }

    // When each newly-live tower and house lights up, in ms from now.
    const towerArrivals: number[] = [];
    const houseArrivals: number[] = [];

    // The plant is `.energized` whenever any tower beyond it is reached —
    // that's when the machine is doing work, and it drives the subliminal
    // stack shimmer in board.css (§8 item 6).
    const plantEnergized = net.energized.size > 1;
    drawables.push({
      r: puzzle.plant.r,
      kind: 1,
      make: () =>
        el(
          'use',
          {
            href: SPRITE.plant,
            x: cx(puzzle.plant),
            y: cy(puzzle.plant),
            class: 'plant-sprite' + (plantEnergized ? ' energized' : ''),
          },
          this.gSprites,
        ),
    });

    towers.forEach((t, i) => {
      const idx = i + 1;
      const live = net.energized.has(idx);
      const role = roles[idx];
      if (live) liveTowerKeys.add(key(t));
      const isNew = live && !this.prevLiveTowers.has(key(t));
      if (isNew) towerArrivals.push(net.dist[idx] * RATE);
      // The §8.2 settle plays only on the sprite this tap raised: the next
      // render rebuilds the node without the class, so it can never replay.
      const fresh = opts.justBuilt?.r === t.r && opts.justBuilt?.c === t.c;
      drawables.push({
        r: t.r,
        kind: 1,
        make: () => {
          const cls = 'tower' + (live && !isNew ? ' energized' : '') + (fresh ? ' just-built' : '');
          const side =
            facings[idx] === 'side' &&
            (role === 'standard' || role === 'junction' || role === 'crossing');
          // The whole assembly: the body plus any dead-end face hardware.
          // Towers carry NO halo twin: a lattice's close parallel lines
          // smear into haze when widened under themselves, so the structure
          // light belongs to the solid silhouettes alone (STYLEGUIDE §5) —
          // a tower's energized step is its linework color and amber tips.
          const hrefs: string[] = [
            side ? SPRITE[`${role}Side`] : role === 'plant' ? SPRITE.plant : SPRITE[role],
          ];
          // a dead-end anchor's terminals appear only on the faces its
          // spans actually land on; they ride the tower's own state classes
          for (const face of deadendSides.get(idx) ?? []) {
            hrefs.push(face === 'left' ? SPRITE.deadendFaceL : SPRITE.deadendFaceR);
          }
          const parts: SVGElement[] = [];
          for (const href of hrefs) {
            parts.push(el('use', { href, x: cx(t), y: cy(t), class: cls }, this.gSprites));
          }
          // The giant's blinking lamps ride along as real elements — inside
          // the sprite def the blink never reaches the <use> clones (see
          // sprites.ts). Phase comes off the document clock, so the per-tap
          // sprite rebuild doesn't restart the blink.
          if (role === 'crossing' && !this.reduceMotion) {
            const phase = `-${Math.round(performance.now() % 1700)}ms`;
            for (const seat of crossingBeaconSeats(side)) {
              el(
                'use',
                {
                  href: SPRITE.beaconLamp,
                  x: cx(t) + seat.x,
                  y: cy(t) + seat.y,
                  class: 'beacon-lamp',
                  style: `--beacon-phase:${phase}`,
                },
                this.gSprites,
              );
            }
          }
          if (isNew) {
            this.surge.later(
              () => parts.forEach((u) => u.classList.add('energized')),
              net.dist[idx] * RATE,
            );
          }
        },
      });
    });

    for (const hl of net.houseLinks) {
      const hk = 'h' + key(hl.h);
      const isNew = hl.lit && !this.prevLitHouses.has(hk);
      if (isNew) houseArrivals.push(hl.arrive * RATE);
      drawables.push({
        r: hl.h.r,
        kind: 1,
        make: () => {
          const u = el(
            'use',
            {
              href: SPRITE.house,
              x: cx(hl.h),
              y: cy(hl.h),
              class: 'house-sprite' + (hl.lit && !isNew ? ' lit' : ''),
            },
            this.gSprites,
          );
          // The windows ride their own overlay so the glow can belong to them
          // alone — a filter on the full house would halo the silhouette.
          const w = el(
            'use',
            {
              href: SPRITE.houseGlow,
              x: cx(hl.h),
              y: cy(hl.h),
              class: 'house-glow' + (hl.lit && !isNew ? ' lit' : ''),
            },
            this.gSprites,
          );
          if (isNew) {
            this.surge.later(() => {
              u.classList.add('lit', 'just-lit');
              w.classList.add('lit', 'just-lit');
            }, hl.arrive * RATE);
          }
        },
      });
    }

    drawables.sort((a, b) => a.r - b.r || a.kind - b.kind).forEach((s) => s.make());
    this.scheduleSurgeSounds(towerArrivals, houseArrivals);

    // Sync chimney smoke to lit state. Newly-lit houses get `.lit` on the
    // surge timeline (at the same tick as their window flick); newly-dark
    // houses lose it immediately — the smoke goes out with the lights.
    for (const hl of net.houseLinks) {
      const hk = key(hl.h);
      const g = this.smokeByHouse.get(hk);
      if (!g) continue;
      const litKey = 'h' + hk;
      const wasLit = this.prevLitHouses.has(litKey);
      const isLit = hl.lit;
      if (isLit && !wasLit) {
        // Newly lit — fade in on the surge timeline.
        this.surge.later(() => g.classList.add('lit'), hl.arrive * RATE);
      } else if (!isLit && wasLit) {
        g.classList.remove('lit');
      } else if (isLit) {
        // Already lit — ensure the class is present (survives a rerender).
        g.classList.add('lit');
      } else {
        g.classList.remove('lit');
      }
    }

    // Ambient inhabitants update after the surge memory has been read from
    // prev* but before it's written for next tick.
    this.inhabitants?.update(net, plantEnergized, this.reduceMotion);

    this.prevLiveWires = liveWireKeys;
    this.prevLiveTowers = liveTowerKeys;
    this.prevLitHouses = litHouseKeys;

    return { net, maxDelay: this.reduceMotion ? 0 : maxDelay };
  }

  /**
   * Set the map panel's wash for the operator's local hour (STYLEGUIDE §1).
   * The sky band retired with the second edition; the hour survives as
   * `--hour-tint`, which board.css mixes ~10% into the panel surface. Called
   * at mount and on the midnight-rollover check; a tint change is one paint,
   * no re-render.
   */
  paintHourTint(hour: number): void {
    const host = this.svg.parentElement ?? this.svg;
    host.style.setProperty('--hour-tint', hourTintFor(hour));
  }

  /**
   * Intensify the amber-hot in every lit-house window on the perfect-grid
   * fanfare tick. Reads as an intensification of the existing house-lit beat
   * (§8), scheduled by the controller from the same clock as the audio cue
   * so the picture and the pitch fire together.
   */
  flarePerfectWindows(): void {
    if (this.reduceMotion) return;
    const glows = this.gSprites.querySelectorAll('use.house-glow.lit');
    glows.forEach((g) => {
      g.classList.remove('perfect');
      // force a reflow so re-adding replays the keyframes
      void (g as SVGElement).getBoundingClientRect();
      g.classList.add('perfect');
      setTimeout(() => g.classList.remove('perfect'), 1200);
    });
  }

  /**
   * Lift the removed tower's sprite out before the teardown wipes it, so
   * demolition gets the settle reversed (STYLEGUIDE §8.2). Skipped under
   * reduced motion: the stylesheet kill switch cannot clear an orphaned node,
   * and a frozen ghost is worse than an instant disappearance.
   */
  private retireTower(p?: Point): void {
    if (!p || this.reduceMotion) return;
    // querySelectorAll: a dead-end anchor is a body plus its terminal
    // overlays, and the whole assembly lifts out together.
    const gone = this.gSprites.querySelectorAll(`use.tower[x="${cx(p)}"][y="${cy(p)}"]`);
    for (const u of gone) {
      this.gFx.appendChild(u);
      u.classList.remove('just-built');
      u.classList.add('tower-out');
      // A plain timeout, not surge.later(): the fade must outlive the next
      // tap's surge.clear() or an invisible node would strand in the fx layer.
      setTimeout(() => u.remove(), RATE * CELL + 60);
    }
  }
}

/**
 * The hour's tone for the map panel wash. Night is the default look
 * (STYLEGUIDE §1); dawn and dusk carry small warm shifts, and the day sits
 * one step lifted from ink. Diluted to ~10% by the panel's color-mix, so the
 * whole range stays a subtle acknowledgement, not a day/night illustration —
 * tests/unit/contrast.test.ts holds every mixed hour to the night register.
 */
export const HOUR_TINTS = [
  { until: 5, tone: '#080e1a' }, // deep night (ink)
  { until: 6, tone: '#0d1424' }, // pre-dawn
  { until: 7, tone: '#13182a' }, // dawn
  { until: 9, tone: '#182236' }, // morning
  { until: 17, tone: '#1a2438' }, // day
  { until: 19, tone: '#191b30' }, // dusk warm
  { until: 21, tone: '#12162a' }, // evening settling
  { until: 24, tone: '#080e1a' }, // back to ink for late night
] as const;

function hourTintFor(hour: number): string {
  return (HOUR_TINTS.find((s) => hour < s.until) ?? HOUR_TINTS[HOUR_TINTS.length - 1]).tone;
}
